import { useState } from 'react'
import { FormFieldsEdit, WellEndPointFields } from '../../../types/form-types'
import { SetValue, ValidationErrors } from '../../hooks/use-edit-form'
import useInlineMap, { CoordinateTypes, LOCATION_FIELD_DEFAULT } from '../../hooks/use-inline-map'
import { defaultIcon } from '../../services/generic-search'
import { InlineMap } from '../aoraki/maps-react/src/components/'
import { TOOLTIP } from '../../../constants/tooltips'
import LookupField from './lookup-field'
import { preventChangeOnScroll } from '../../services/form-utils'
import { getBaseLayers } from '../../config/map'
import { FieldTypes, FormField, FormFieldRow, Heading, ReadOnlyField, TextInput } from '@msaf/core-react'
import { latLngToNZTM, nztmToLatLng, validateLatLng } from '@msaf/maps-common'

export type LocationFieldEditType = {
  fieldId:
    | keyof Pick<
        FormFieldsEdit,
        | 'actualEasting'
        | 'actualNorthing'
        | 'proposedEasting'
        | 'proposedNorthing'
        | 'actualLongitude'
        | 'actualLatitude'
        | 'proposedLongitude'
        | 'proposedLatitude'
        | 'galleryStartLatitude'
        | 'galleryStartLongitude'
        | 'galleryEndLatitude'
        | 'galleryEndLongitude'
        | 'galleryEndEasting'
        | 'galleryEndNorthing'
        | 'galleryStartEasting'
        | 'galleryStartNorthing'
      >
    | keyof Pick<WellEndPointFields, 'easting' | 'northing' | 'latitude' | 'longitude'>
  value: string
  setValue: (value: FieldTypes) => void
}

export interface LocationFieldEditProps {
  formFieldLabel?: string
  labelledBy: string
  easting: LocationFieldEditType
  northing: LocationFieldEditType
  latitude: LocationFieldEditType
  longitude: LocationFieldEditType
  isLoading?: boolean
  isMapHidden?: boolean
  validationFieldErrors?: ValidationErrors
  isRequired?: boolean
  index?: number
  setValue?: SetValue<Pick<FormFieldsEdit, 'locationAccuracy'>>
  locationAccuracy?: number
  isWellGrantedAsLegacy?: boolean
  showLocationAccuracy?: boolean
  distanceBetweenProposedAndActualWell?: { value: number | undefined; canRender: boolean }
}

export const getLocationFieldtoolTipMessage = (key: string) => {
  if (key in TOOLTIP) {
    return TOOLTIP[key as keyof typeof TOOLTIP]
  }
}

export default function LocationFieldEdit({
  formFieldLabel,
  easting,
  northing,
  index,
  isLoading,
  isMapHidden = false,
  validationFieldErrors = {},
  isRequired = false,
  latitude,
  longitude,
  setValue,
  locationAccuracy,
  isWellGrantedAsLegacy,
  showLocationAccuracy = false,
  distanceBetweenProposedAndActualWell,
}: LocationFieldEditProps) {
  const { startPosition, projectionRef, isValidCoordinates, setIsValidCoordinates } = useInlineMap({
    xCoordinate: easting.value ?? longitude.value,
    yCoordinate: northing.value ?? latitude.value,
    projectionType:
      !easting.value && !northing.value && validateLatLng(latitude.value, longitude.value)
        ? CoordinateTypes.LatLng
        : CoordinateTypes.NZTM,
  })

  // Keeps track of changes made through the map interface or the form inputs
  const [isLocationSetFromMap, setIsLocationSetFromMap] = useState(false)

  const convertMapCoordinates = (coordinates: [number, number]) => {
    let [x, y] = coordinates
    // Using the projectionRef value instead of the props to avoid using stale props due to closure
    if (projectionRef.current !== CoordinateTypes.LatLng) {
      try {
        ;[x, y] = latLngToNZTM(coordinates)
      } catch {
        setIsValidCoordinates(false)
      }
    }
    return [x, y]
  }

  // Set the easting northing value in the form fields
  const updateLocationFromMap = (coordinatesInLatLng: [number, number]) => {
    let [y, x] = coordinatesInLatLng
    latitude.setValue(y.toFixed(5))
    longitude.setValue(x.toFixed(5))
    if (x && y) {
      // Convert value based on projection type
      // Assumes NZTM as the default projection type
      ;[x, y] = convertMapCoordinates(coordinatesInLatLng)
      easting.setValue(x.toFixed(2))
      northing.setValue(y.toFixed(2))
      // Coordinates updated through the map UI
      setIsLocationSetFromMap(true)
    }
  }

  const setEasting = (value: string) => {
    easting.setValue(Number(value).toFixed(2))
    try {
      if (northing.value) {
        const [lat, lng] = nztmToLatLng([Number(value), Number(northing.value)])
        longitude.setValue(Number(lng).toFixed(5))
        latitude.setValue(Number(lat).toFixed(5))
      }
    } catch (err) {
      setIsValidCoordinates(false)
    }
    // Coordinates updated through the form input
    setIsLocationSetFromMap(false)
  }

  const setNorthing = (value: string) => {
    northing.setValue(Number(value).toFixed(2))
    try {
      if (easting.value) {
        const [lat, lng] = nztmToLatLng([Number(easting.value), Number(value)])
        latitude.setValue(Number(lat).toFixed(5))
        longitude.setValue(Number(lng).toFixed(5))
      }
    } catch {
      setIsValidCoordinates(false)
    }
    // Coordinates updated through the form input
    setIsLocationSetFromMap(false)
  }

  const setLongitude = (value: string) => {
    longitude.setValue(Number(value).toFixed(5))
    try {
      if (latitude.value) {
        const [east, north] = latLngToNZTM([Number(latitude.value), Number(value)])
        easting.setValue(Number(east).toFixed(2))
        northing.setValue(Number(north).toFixed(2))
      }
    } catch {
      setIsValidCoordinates(false)
    }
    // Coordinates updated through the form input
    setIsLocationSetFromMap(false)
  }

  const setLatitude = (value: string) => {
    latitude.setValue(Number(value).toFixed(5))
    try {
      if (longitude.value) {
        const [east, north] = latLngToNZTM([Number(value), Number(longitude.value)])
        northing.setValue(Number(north).toFixed(2))
        easting.setValue(Number(east).toFixed(2))
      }
    } catch {
      setIsValidCoordinates(false)
    }
    // Coordinates updated through the form input
    setIsLocationSetFromMap(false)
  }

  return (
    <>
      {formFieldLabel && (
        <Heading isSkeleton={isLoading} level={4}>
          {formFieldLabel}
        </Heading>
      )}
      <FormFieldRow>
        <FormField
          toolTipMessage={getLocationFieldtoolTipMessage(longitude.fieldId)}
          isRequired={isRequired}
          isSkeleton={isLoading}
          label={CoordinateTypes.Lng}
          htmlFor={`${longitude.fieldId}${index !== undefined ? index + 1 : ''}`}
          validationMessages={
            longitude.fieldId in validationFieldErrors
              ? validationFieldErrors[longitude.fieldId as keyof typeof validationFieldErrors]
              : undefined
          }
        >
          <TextInput
            type='number'
            onWheel={preventChangeOnScroll}
            isSkeleton={isLoading}
            id={`${longitude.fieldId}${index !== undefined ? index + 1 : ''}`}
            value={`${longitude.value ?? ''}`}
            setValue={(value) => setLongitude(value)}
            isInvalid={
              isValidCoordinates === false ||
              !!validationFieldErrors[longitude.fieldId as keyof typeof validationFieldErrors]
            }
            min='-180.00000'
            max='180.00000'
            step='0.10000'
          />
        </FormField>
        <FormField
          toolTipMessage={getLocationFieldtoolTipMessage(latitude.fieldId)}
          isRequired={isRequired}
          isSkeleton={isLoading}
          label={CoordinateTypes.Lat}
          htmlFor={`${latitude.fieldId}${index !== undefined ? index + 1 : ''}`}
          validationMessages={
            latitude.fieldId in validationFieldErrors
              ? validationFieldErrors[latitude.fieldId as keyof typeof validationFieldErrors]
              : undefined
          }
        >
          <TextInput
            type='number'
            onWheel={preventChangeOnScroll}
            isSkeleton={isLoading}
            id={`${latitude.fieldId}${index !== undefined ? index + 1 : ''}`}
            value={latitude.value}
            setValue={(value) => setLatitude(value)}
            isInvalid={
              isValidCoordinates === false ||
              !!validationFieldErrors[latitude.fieldId as keyof typeof validationFieldErrors]
            }
            min='-90.00000'
            max='90.00000'
            step='0.10000'
          />
        </FormField>
      </FormFieldRow>

      <FormFieldRow>
        <FormField
          toolTipMessage={getLocationFieldtoolTipMessage(easting.fieldId)}
          isRequired={isRequired}
          isSkeleton={isLoading}
          label={CoordinateTypes.Easting}
          htmlFor={`${easting.fieldId}${index !== undefined ? index + 1 : ''}`}
          validationMessages={
            easting.fieldId in validationFieldErrors
              ? validationFieldErrors[easting.fieldId as keyof typeof validationFieldErrors]
              : undefined
          }
        >
          <TextInput
            type='number'
            onWheel={preventChangeOnScroll}
            isSkeleton={isLoading}
            id={`${easting.fieldId}${index !== undefined ? index + 1 : ''}`}
            value={`${easting.value ?? ''}`}
            setValue={(value) => setEasting(value)}
            isInvalid={
              isValidCoordinates === false ||
              !!validationFieldErrors[easting.fieldId as keyof typeof validationFieldErrors]
            }
            min='1000000'
            max='2100000'
            step='1'
          />
        </FormField>
        <FormField
          toolTipMessage={getLocationFieldtoolTipMessage(northing.fieldId)}
          isRequired={isRequired}
          isSkeleton={isLoading}
          label={CoordinateTypes.Northing}
          htmlFor={`${northing.fieldId}${index !== undefined ? index + 1 : ''}`}
          validationMessages={
            northing.fieldId in validationFieldErrors
              ? validationFieldErrors[northing.fieldId as keyof typeof validationFieldErrors]
              : undefined
          }
        >
          <TextInput
            type='number'
            onWheel={preventChangeOnScroll}
            isSkeleton={isLoading}
            id={`${northing.fieldId}${index !== undefined ? index + 1 : ''}`}
            value={`${northing.value ?? ''}`}
            setValue={(value) => setNorthing(value)}
            isInvalid={
              isValidCoordinates === false ||
              !!validationFieldErrors[northing.fieldId as keyof typeof validationFieldErrors]
            }
            min='4700000'
            max='6300000'
            step='1'
          />
        </FormField>
      </FormFieldRow>
      {setValue && showLocationAccuracy && (
        <FormField
          isSkeleton={isLoading}
          label='Location quality'
          labelId='locationAccuracy'
          validationMessages={validationFieldErrors?.locationAccuracy}
          isRequired={!isWellGrantedAsLegacy && isRequired}
        >
          <LookupField
            isSkeleton={isLoading}
            labelledBy='locationAccuracy'
            fieldId='locationAccuracy'
            value={`${locationAccuracy ?? ''}`}
            setValue={({ value }) => setValue('locationAccuracy', value)}
            isInvalid={!!validationFieldErrors?.locationAccuracy}
          />
        </FormField>
      )}
      {distanceBetweenProposedAndActualWell?.canRender && (
        <FormField
          isSkeleton={isLoading}
          labelId='distanceBetweenProposedAndActualWell'
          label='Distance from proposed (m)'
        >
          <ReadOnlyField
            isSkeleton={isLoading}
            labelledBy='distanceBetweenProposedAndActualWell'
            value={distanceBetweenProposedAndActualWell?.value ?? '-'}
          />
        </FormField>
      )}
      {!isMapHidden && (
        <InlineMap
          isSkeleton={isLoading}
          setLocation={updateLocationFromMap}
          position={startPosition}
          center={startPosition}
          markerIcon={defaultIcon}
          getBaseLayers={getBaseLayers}
          gotoStartPosition={!isLocationSetFromMap && !!startPosition}
          zoom={startPosition ? LOCATION_FIELD_DEFAULT.LOCATION_SET : LOCATION_FIELD_DEFAULT.NO_LOCATION}
          showMarker={!!startPosition}
          draggingMarkerUpdatesLocation
        />
      )}
    </>
  )
}
